import type { DirectoryRole } from "./access.ts";

export const ROLES_FILTER_COLUMNS = ["role", "scope"] as const;
export type RolesFilterColumn = (typeof ROLES_FILTER_COLUMNS)[number];

export type RolesFilters = { readonly values: ReadonlyMap<RolesFilterColumn, ReadonlySet<string>> };
export const NO_ROLES_FILTERS: RolesFilters = { values: new Map() };

export type RolesSort = { readonly column: RolesFilterColumn; readonly direction: "asc" | "desc" };
export const DEFAULT_ROLES_SORT: RolesSort = { column: "role", direction: "asc" };

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export function roleFacetValue(role: DirectoryRole, column: RolesFilterColumn): string {
  return column === "role" ? role.name : role.scope;
}

function rowFacets(role: DirectoryRole): ReadonlyMap<RolesFilterColumn, string> {
  return new Map(ROLES_FILTER_COLUMNS.map((column) => [column, roleFacetValue(role, column)]));
}

function passes(facets: ReadonlyMap<RolesFilterColumn, string>, column: RolesFilterColumn, filters: RolesFilters): boolean {
  const selected = filters.values.get(column);
  if (!selected || selected.size === 0) return true;
  return selected.has(facets.get(column) ?? "");
}

export function filterRoleRows(rows: readonly DirectoryRole[], filters: RolesFilters): DirectoryRole[] {
  return rows.filter((row) => {
    const facets = rowFacets(row);
    return ROLES_FILTER_COLUMNS.every((column) => passes(facets, column, filters));
  });
}

export type RolesColumnFacet = { readonly options: readonly { value: string; label: string; count: number }[] };
export type RolesFacets = { readonly rows: readonly DirectoryRole[]; readonly columns: ReadonlyMap<RolesFilterColumn, RolesColumnFacet> };

export function computeRolesFacets(rows: readonly DirectoryRole[], filters: RolesFilters): RolesFacets {
  const counts = new Map<RolesFilterColumn, Map<string, number>>(ROLES_FILTER_COLUMNS.map((column) => [column, new Map()]));
  const passing: DirectoryRole[] = [];
  for (const row of rows) {
    const facets = rowFacets(row);
    const failed = ROLES_FILTER_COLUMNS.filter((column) => !passes(facets, column, filters));
    if (failed.length === 0) passing.push(row);
    for (const column of ROLES_FILTER_COLUMNS) {
      const counted = failed.length === 0 || (failed.length === 1 && failed[0] === column);
      const tally = counts.get(column);
      if (!tally) continue;
      const value = facets.get(column) ?? "";
      tally.set(value, (tally.get(value) ?? 0) + (counted ? 1 : 0));
    }
  }
  const columns = new Map<RolesFilterColumn, RolesColumnFacet>();
  for (const column of ROLES_FILTER_COLUMNS) {
    const tally = counts.get(column) ?? new Map<string, number>();
    columns.set(column, {
      options: [...tally.keys()].sort((a, b) => collator.compare(a, b)).map((value) => ({ value, label: value, count: tally.get(value) ?? 0 })),
    });
  }
  return { rows: passing, columns };
}

function filterSignature(filters: RolesFilters): string {
  const parts: string[] = [];
  for (const column of ROLES_FILTER_COLUMNS) {
    const selected = filters.values.get(column);
    if (!selected || selected.size === 0) continue;
    parts.push(column, ...[...selected].sort());
  }
  return parts.join("\u0000");
}

export function createRolesFacetMemo() {
  let last: { rows: readonly DirectoryRole[]; signature: string; facets: RolesFacets } | null = null;
  return (rows: readonly DirectoryRole[], filters: RolesFilters): RolesFacets => {
    const signature = filterSignature(filters);
    if (last && last.rows === rows && last.signature === signature) return last.facets;
    const facets = computeRolesFacets(rows, filters);
    last = { rows, signature, facets };
    return facets;
  };
}

export function toggleRolesFilter(filters: RolesFilters, column: RolesFilterColumn, value: string): RolesFilters {
  const values = new Map(filters.values);
  const selected = new Set(values.get(column) ?? []);
  if (selected.has(value)) selected.delete(value);
  else selected.add(value);
  if (selected.size === 0) values.delete(column);
  else values.set(column, selected);
  return { values };
}

export function selectedRolesFilterCount(filters: RolesFilters, column: RolesFilterColumn): number {
  return filters.values.get(column)?.size ?? 0;
}

export function hasRolesFilters(filters: RolesFilters): boolean {
  return [...filters.values.values()].some((selected) => selected.size > 0);
}

export function sortRoleRows(rows: readonly DirectoryRole[], sort: RolesSort): DirectoryRole[] {
  const copy = [...rows];
  const direction = sort.direction === "asc" ? 1 : -1;
  copy.sort((left, right) => collator.compare(roleFacetValue(left, sort.column), roleFacetValue(right, sort.column)) * direction);
  return copy;
}

export const ROLES_COLUMN_LABELS: Readonly<Record<RolesFilterColumn, string>> = {
  role: "Role",
  scope: "Scope",
};
